import { Customer } from "@/models/response/customer";
import customerService from "@/services/customer";
import customerGroupService from "@/services/customerGroup";
import { Button, Skeleton, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconSearch, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import BottomSheet from "./BottomSheet";
import { closeSheet } from "./groups";
import { customerName, faNumber, formatPhone } from "./sms";
import classes from "./sms.module.scss";

type Props = {
  opened: boolean;
  onClose: () => void;
  groupId: number;
  groupName?: string;
  onAdded: () => void;
};

export const ADD_MEMBERS_SHEET = "addMembers";

export default function AddMembersSheet({ opened, onClose, groupId, groupName, onAdded }: Props) {
  const queryClient = useQueryClient();
  const [term, setTerm] = useState("");
  const [debounced] = useDebouncedValue(term.trim(), 400);
  const [selected, setSelected] = useState<Map<number, Customer>>(new Map());

  const customers = useQuery({
    queryKey: ["groupMemberPicker", debounced],
    queryFn: () => customerService.getCustomers({ Term: debounced, Count: 40, Skip: 0 }),
    enabled: opened,
    keepPreviousData: true,
  });
  // Marks people who are already in the group; the bulk endpoint skips them anyway.
  const members = useQuery({
    queryKey: ["customerGroupMemberIds", groupId],
    queryFn: () => customerGroupService.getMembers({ customerGroupId: groupId, Count: 1000, Skip: 0 }),
    enabled: opened && groupId > 0,
  });
  const memberIds = new Set((members.data?.result ?? []).map((member) => member.id));

  const rows = customers.data?.result ?? [];
  const toggle = (customer: Customer) =>
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(customer.id!)) next.delete(customer.id!);
      else next.set(customer.id!, customer);
      return next;
    });
  const selectAll = () =>
    setSelected((current) => {
      const next = new Map(current);
      rows.filter((c) => c.id && !memberIds.has(c.id)).forEach((c) => next.set(c.id!, c));
      return next;
    });

  const add = useMutation({
    mutationFn: () => customerGroupService.addMembers(groupId, [...selected.keys()]),
    onSuccess: () => {
      notifications.show({ color: "green", message: `${faNumber(selected.size)} مشتری به گروه اضافه شد` });
      setSelected(new Map());
      setTerm("");
      queryClient.invalidateQueries({ queryKey: ["customerGroup"] });
      queryClient.invalidateQueries({ queryKey: ["customerGroupMembers"] });
      queryClient.invalidateQueries({ queryKey: ["customerGroupMemberIds", groupId] });
      onAdded();
      closeSheet(ADD_MEMBERS_SHEET, onClose);
    },
  });

  const count = selected.size;
  return (
    <BottomSheet opened={opened} onClose={onClose} name={ADD_MEMBERS_SHEET} tall title={groupName ? `افزودن به «${groupName}»` : "افزودن اعضا"}>
      <div className={classes.noticeText} style={{ gap: 12, flex: 1 }}>
        <TextInput
          aria-label="جستجوی مشتری"
          placeholder="نام یا شماره‌ی موبایل"
          size="md"
          radius={14}
          leftSection={<IconSearch size={19} stroke={1.8} />}
          value={term}
          onChange={(event) => setTerm(event.currentTarget.value)}
        />
        {count > 0 && (
          <div className={classes.chipRow}>
            {[...selected.values()].map((customer) => (
              <span key={customer.id} className={classes.selectedChip}>
                {customerName(customer) || formatPhone(customer.phoneNumber)}
                <button type="button" aria-label={`برداشتن ${customerName(customer) || customer.phoneNumber}`} onClick={() => toggle(customer)}>
                  <IconX size={14} stroke={2.4} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className={classes.pickHead}>
          <span>{debounced ? `${faNumber(rows.length)} نتیجه` : "مشتریان شما"}</span>
          {rows.length > 0 && (
            <button type="button" className={`${classes.textButton} ${classes.primaryText}`} onClick={selectAll}>
              انتخاب همه‌ی این فهرست
            </button>
          )}
        </div>

        <div className={classes.pickList}>
          {customers.isLoading
            ? [0, 1, 2, 3].map((i) => <Skeleton key={i} height={52} radius="md" mb={10} />)
            : rows.map((customer) => {
                const member = memberIds.has(customer.id);
                const checked = member || selected.has(customer.id!);
                const name = customerName(customer);
                return (
                  <label key={customer.id} className={classes.pickRow} data-checked={checked} data-disabled={member}>
                    <input type="checkbox" className={classes.srOnly} checked={checked} disabled={member} onChange={() => toggle(customer)} />
                    <span className={classes.checkBox}>{checked && <IconCheck size={15} stroke={3} />}</span>
                    <span className={classes.memberText}>
                      <span className={classes.memberName}>{name || formatPhone(customer.phoneNumber)}</span>
                      {name && <span className={classes.memberPhone}>{formatPhone(customer.phoneNumber)}</span>}
                    </span>
                    {member && <span className={classes.memberTag}>عضو گروه</span>}
                  </label>
                );
              })}
          {!customers.isLoading && rows.length === 0 && (
            <p className={classes.intro} style={{ textAlign: "center", padding: "24px 0" }}>
              {debounced ? "مشتری‌ای با این نام یا شماره پیدا نشد." : "هنوز مشتری‌ای ثبت نکرده‌اید."}
            </p>
          )}
        </div>

        <div className={classes.sheetFooter} style={{ marginTop: "auto" }}>
          <Button fullWidth h={52} radius={14} color="myColor.7" fz={15} fw={800} disabled={count === 0} loading={add.isLoading} onClick={() => add.mutate()}>
            {count === 0 ? "مشتری‌ای انتخاب نشده" : `افزودن ${faNumber(count)} مشتری به گروه`}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}
